/**
 * The project Home's body as gadgets (utils/homeGadgetsModel.js): the blocks the caller passes in `gadgets` (by id) in
 * a 3-column grid, in the person's order and sizes. "Edit dashboard" shows, on each gadget, a handle to drag it (↑ / ↓
 * on the focused handle), size S · M · L and Hide; "Add gadget" brings a hidden one back; "Reset" goes back to the default. A gadget with nothing to
 * show (`null`) is left out, so an empty block never leaves a hole. `model` (utils/homeGadgetsModel.js
 * createGadgetsModel) says which gadgets exist and where the person's choices are kept: the project Home's by default,
 * the workspace Overview's when it passes its own.
 *
 * A block can also be a function `({ settings, onSettings }) => node`: a gadget with its own settings (the File gadget's
 * chosen file), kept with the layout (model settingsOf / withSettings).
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { SIZES, SIZE_SPAN, homeGadgetsModel } from '../../utils/homeGadgetsModel';
import './HomeGadgets.css';
import './gadgets/Gadgets.css';

/**
 * A thumbnail of what a gadget looks like, for the Add gadget menu: a few grey shapes in the gadget's layout (rows, bars,
 * a progress bar, a drop area, folders, a number), never real data.
 */
export function GadgetPreview({ kind }) {
  const rows = (n, dot = false) => Array.from({ length: n }, (_, i) => (
    <span className="hgp-row" key={i}>{dot && <i className="hgp-dot" />}<i className="hgp-line" style={{ width: `${70 - i * 12}%` }} /></span>
  ));
  const body = {
    list: rows(3),
    status: rows(3, true),
    bars: <span className="hgp-bars">{[40, 70, 30, 90, 55, 65, 20, 80].map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</span>,
    progress: <>{<span className="hgp-progress"><i style={{ width: '65%' }} /></span>}{rows(2, true)}</>,
    drop: <><span className="hgp-drop" />{rows(2)}</>,
    folders: rows(3, true),
    number: <><span className="hgp-number" />{<span className="hgp-progress"><i style={{ width: '35%' }} /></span>}</>,
    // The kinds of the gadgets added later (components/home/gadgets): a page thumbnail, a small table, people and
    // agents, a run trend, number tiles, stages, a timeline, a comment.
    frame: <span className="hgp-frame" />,
    table: <span className="hgp-table">{Array.from({ length: 9 }, (_, i) => <i key={i} />)}</span>,
    avatars: <><span className="hgp-avatars"><i /><i /><i /></span>{rows(2)}</>,
    trend: <><span className="hgp-trend">{[60, 80, 70, 90, 40, 85, 95].map((h, i) => <i className={i === 4 ? 'is-bad' : ''} key={i} style={{ height: `${h}%` }} />)}</span>{rows(1)}</>,
    tiles: <span className="hgp-tiles"><i /><i /><i /></span>,
    stages: <><span className="hgp-stages"><i /><i /><i /></span>{rows(2)}</>,
    timeline: <><span className="hgp-timeline"><b /><i /><b /><i /><b /></span>{rows(2)}</>,
    thread: <><span className="hgp-thread" />{rows(2, true)}</>,
    // A document: a heading, then text.
    doc: <><span className="hgp-heading" />{rows(3)}</>,
  }[kind] || rows(3);
  return <span aria-hidden="true" className={`hgp hgp-kind-${kind}`} data-testid={`gadget-preview-${kind}`}>{body}</span>;
}
GadgetPreview.propTypes = { kind: PropTypes.string };

export default function HomeGadgets({ gadgets, barSlot = null, model = homeGadgetsModel, buttonClass = 'ph-btn', primaryClass = 'ph-btn-primary' }) {
  const {
    GADGETS_EVENT, hiddenGadgets, moveGadget, moveGadgetTo, readGadgetPrefs, resetGadgetPrefs, saveGadgetPrefs, visibleGadgets, withHidden, withShown, withSize, withTall,
    settingsOf = (prefs, id) => prefs?.settings?.[id] || {}, withSettings = (prefs, id, value) => ({ ...prefs, settings: { ...(prefs.settings || {}), [id]: value } }),
  } = model;
  const [prefs, setPrefs] = useState(readGadgetPrefs);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  // The gadget being dragged and the one it is over (HTML drag and drop; the arrows below are the keyboard way).
  const [drag, setDrag] = useState({ id: null, over: null });
  // The Add gadget gallery is drawn on the page body, under its button, so no gadget or card can cover it: a grid of 3
  // columns on a wide screen, 2 on a medium one, and on a phone a bottom sheet with Close (HomeGadgets.css).
  const addRef = useRef(null);
  const editRef = useRef(null);
  const menuRef = useRef(null);
  const [menuAt, setMenuAt] = useState({ top: 0, right: 0 });
  const toggleAdding = () => {
    const box = addRef.current?.getBoundingClientRect();
    if (box) { setMenuAt({ top: box.bottom + 4, right: Math.max(8, window.innerWidth - box.right) }); }
    setAdding(!adding);
  };
  // Closing it gives the focus back to "+ Add gadget" (or to Done, once nothing is left to add).
  const closeAdding = () => {
    setAdding(false);
    setTimeout(() => (addRef.current || editRef.current)?.focus?.(), 0);
  };
  // Escape, a click elsewhere or scrolling the page closes it; scrolling its own list does not.
  useEffect(() => {
    if (!adding) { return undefined; }
    const onKey = (event) => { if (event.key === 'Escape') { closeAdding(); } };
    const onDown = (event) => { if (!event.target.closest?.('.hg-add-menu, [data-testid="gadget-add"]')) { setAdding(false); } };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    const onScroll = (event) => { if (!event.target.closest?.('.hg-add-menu')) { setAdding(false); } };
    window.addEventListener('scroll', onScroll, true);
    // The first gadget takes the focus, so the arrows and Tab start in the gallery.
    menuRef.current?.querySelector('.hg-add-row')?.focus();
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); window.removeEventListener('scroll', onScroll, true); };
  }, [adding]);
  // The arrows move between the gadgets of the gallery, a row up or down by as many as its columns; Home / End.
  const onGalleryKey = (event) => {
    const rows = Array.from(menuRef.current?.querySelectorAll('.hg-add-row') || []);
    const at = rows.indexOf(document.activeElement);
    if (at < 0) { return; }
    const grid = menuRef.current.querySelector('.hg-add-grid');
    const template = grid && typeof window.getComputedStyle === 'function' ? window.getComputedStyle(grid).gridTemplateColumns : '';
    const columns = Math.max(1, (template || '').split(' ').filter((part) => part && part !== 'none').length);
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns }[event.key];
    let next = null;
    if (step) { next = Math.min(rows.length - 1, Math.max(0, at + step)); }
    if (event.key === 'Home') { next = 0; }
    if (event.key === 'End') { next = rows.length - 1; }
    if (next === null) { return; }
    event.preventDefault();
    rows[next].focus();
  };

  // Another Home (or tab) changed them.
  useEffect(() => {
    const onChange = (event) => setPrefs(event.detail || readGadgetPrefs());
    window.addEventListener(GADGETS_EVENT, onChange);
    return () => window.removeEventListener(GADGETS_EVENT, onChange);
  }, [GADGETS_EVENT]);

  const change = (next) => setPrefs(saveGadgetPrefs(next));
  const shown = visibleGadgets(prefs).filter((gadget) => gadgets[gadget.id] || editing);
  const hidden = hiddenGadgets(prefs);
  // A block that is a function gets its settings and a way to save them.
  const blockOf = (id) => (typeof gadgets[id] === 'function'
    ? gadgets[id]({ settings: settingsOf(prefs, id), onSettings: (value) => change(withSettings(prefs, id, value)) })
    : gadgets[id]);

  const bar = (
      <div className="hg-bar">
        {editing && hidden.length > 0 && (
          <span className="hg-add">
            <button aria-controls={adding ? 'hg-add-menu' : undefined} aria-expanded={adding} aria-haspopup="dialog" className={buttonClass} data-testid="gadget-add" onClick={toggleAdding} ref={addRef} type="button">+ Add gadget</button>
            {adding && createPortal((
              <>
                {/* On a phone the page dims behind the sheet; a tap on it closes the sheet (the mousedown above). */}
                <div aria-hidden="true" className="hg-add-backdrop" />
                <div
                  aria-label="Add a gadget"
                  className="hg-add-menu"
                  data-testid="gadget-add-menu"
                  id="hg-add-menu"
                  onKeyDown={onGalleryKey}
                  ref={menuRef}
                  role="dialog"
                  style={{ '--hg-menu-top': `${menuAt.top}px`, '--hg-menu-right': `${menuAt.right}px` }}
                >
                  <div className="hg-add-head">
                    <strong>Add a gadget</strong>
                    <button className="hg-add-close" data-testid="gadget-add-close" onClick={closeAdding} type="button">Close</button>
                  </div>
                  <div className="hg-add-grid" role="list">
                    {hidden.map((gadget) => (
                      <div className="hg-add-cell" key={gadget.id} role="listitem">
                        <button className="hg-add-row" data-testid={`gadget-add-${gadget.id}`} onClick={() => { change(withShown(prefs, gadget.id)); closeAdding(); }} type="button">
                          <GadgetPreview kind={gadget.preview || 'list'} />
                          <span className="hg-add-text">
                            <strong>{gadget.label}</strong>
                            <span>{gadget.desc}</span>
                          </span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ), document.body)}
          </span>
        )}
        {editing && <button className={buttonClass} data-testid="gadget-reset" onClick={() => setPrefs(resetGadgetPrefs())} type="button">Reset</button>}
        <button aria-pressed={editing} className={`${buttonClass}${editing ? ` ${primaryClass}` : ''}`} data-testid="gadget-edit" ref={editRef} onClick={() => { setEditing(!editing); setAdding(false); }} type="button">
          {editing ? 'Done' : 'Edit dashboard'}
        </button>
      </div>
  );

  return (
    <div className="hg" data-testid="home-gadgets">
      {barSlot ? createPortal(bar, barSlot) : bar}
      <div className="hg-grid">
        {shown.map((gadget) => (
          <section
            aria-label={gadget.label}
            className={`hg-item${editing ? ' is-editing' : ''}`}
            data-dragging={drag.id === gadget.id ? 'true' : undefined}
            data-over={drag.id && drag.over === gadget.id && drag.id !== gadget.id ? 'true' : undefined}
            data-size={gadget.size}
            data-tall={gadget.tall ? 'true' : undefined}
            data-testid={`gadget-${gadget.id}`}
            draggable={editing}
            key={gadget.id}
            onDragEnd={() => setDrag({ id: null, over: null })}
            onDragOver={editing ? (event) => { event.preventDefault(); if (drag.over !== gadget.id) { setDrag((now) => ({ ...now, over: gadget.id })); } } : undefined}
            onDragStart={editing ? (event) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', gadget.id); setDrag({ id: gadget.id, over: null }); } : undefined}
            onDrop={editing ? (event) => { event.preventDefault(); if (drag.id) { change(moveGadgetTo(prefs, drag.id, gadget.id)); } setDrag({ id: null, over: null }); } : undefined}
            style={{ '--hg-span': SIZE_SPAN[gadget.size], '--hg-rows': gadget.tall ? 2 : 1 }}
          >
            {editing && (
              <div className="hg-edit" role="toolbar" aria-label={`${gadget.label} layout`}>
                {/* Drag to move; with the keyboard, the handle takes ↑ / ↓ (no buttons of its own). */}
                <span
                  aria-label={`Move ${gadget.label}: drag, or press up or down`}
                  className="hg-grip"
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') { event.preventDefault(); change(moveGadget(prefs, gadget.id, -1)); }
                    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') { event.preventDefault(); change(moveGadget(prefs, gadget.id, 1)); }
                  }}
                  role="button"
                  tabIndex={0}
                  title="Drag to move"
                >⋮⋮</span>
                <span className="hg-edit-name">{gadget.label}</span>
                <span className="hg-sizes" role="group" aria-label={`${gadget.label} size`}>
                  {SIZES.map((size) => (
                    <button aria-pressed={gadget.size === size} className={gadget.size === size ? 'is-on' : ''} key={size} onClick={() => change(withSize(prefs, gadget.id, size))} type="button">{size}</button>
                  ))}
                </span>
                <button aria-label={`${gadget.label} two rows high`} aria-pressed={Boolean(gadget.tall)} className={`hg-tall${gadget.tall ? ' is-on' : ''}`} onClick={() => change(withTall(prefs, gadget.id, !gadget.tall))} title="Two rows high: a tall gadget beside two stacked ones" type="button">Tall</button>
                <button className="hg-hide" onClick={() => change(withHidden(prefs, gadget.id))} type="button">Hide</button>
              </div>
            )}
            {blockOf(gadget.id) || <div className="hg-empty">Nothing to show here yet.</div>}
          </section>
        ))}
      </div>
    </div>
  );
}

HomeGadgets.propTypes = {
  // Where the Edit dashboard buttons go (the Home's title row); none: above the grid.
  barSlot: PropTypes.object,
  // The classes of the bar's buttons: the project Home's by default.
  buttonClass: PropTypes.string,
  primaryClass: PropTypes.string,
  // The gadgets' catalog and preferences (createGadgetsModel); none: the project Home's.
  model: PropTypes.object,
  // The blocks by gadget id (a node, or null/undefined for one with nothing to show), or a function of the gadget's
  // `{ settings, onSettings }` for one with settings.
  gadgets: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.node, PropTypes.func])).isRequired,
};
